// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — TenantManagementPage (Admin)
// Manage tenants and feature flags per tenant
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import {
  Building2, Coffee, ChevronDown, ChevronUp
} from 'lucide-react';
import { useTheme } from '@contexts/ThemeContext';
import { useLang } from '@contexts/LangContext';
import { supabase } from '@lib/supabase';
import { PageHeader } from '@components/ui/PageHeader';
import { Card, Badge, Toggle } from '@components/ui/Primitives';
import { LoadingSpinner } from '@components/ui/States';
import { CLIENT_MODULES } from '@constants/modules';

export function TenantManagementPage() {
  const { theme } = useTheme();
  const { t, lang } = useLang();

  const [tenants, setTenants] = useState([]);
  const [featuresMap, setFeaturesMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    const [{ data: tenantsData }, { data: featuresData }] = await Promise.all([
      supabase.from('tenants').select('*').order('created_at', { ascending: false }),
      supabase.from('feature_flags').select('*')
    ]);

    setTenants(tenantsData || []);

    const fMap = {};
    (featuresData || []).forEach(f => {
      if (!fMap[f.tenant_id]) fMap[f.tenant_id] = {};
      fMap[f.tenant_id][f.module_key] = f.is_enabled;
    });
    setFeaturesMap(fMap);
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const toggleFeature = async (tenantId, moduleKey) => {
    const currentValue = featuresMap[tenantId]?.[moduleKey] || false;
    const newValue = !currentValue;

    // Optimistic update
    setFeaturesMap(prev => ({
      ...prev,
      [tenantId]: {
        ...prev[tenantId],
        [moduleKey]: newValue
      }
    }));

    // Persist
    const { error } = await supabase
      .from('feature_flags')
      .upsert(
        { tenant_id: tenantId, module_key: moduleKey, is_enabled: newValue },
        { onConflict: 'tenant_id,module_key' }
      );

    if (error) {
      // Revert on error
      setFeaturesMap(prev => ({
        ...prev,
        [tenantId]: {
          ...prev[tenantId],
          [moduleKey]: currentValue
        }
      }));
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader
        icon={<Building2 size={22} color={theme.primary} />}
        title={t.tenantManagement}
        description={lang === 'ar' ? 'تفعيل / تعطيل الوحدات لكل عميل' : 'Toggle modules per tenant'}
      />

      {tenants.map(tn => {
        const isExpanded = expandedId === tn.id;
        const enabledCount = Object.values(featuresMap[tn.id] || {})
          .filter(Boolean).length;

        return (
          <Card key={tn.id} style={{ marginBottom: 14, overflow: 'hidden' }}>
            <div
              onClick={() => setExpandedId(isExpanded ? null : tn.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 22px',
                cursor: 'pointer',
                transition: 'background 0.15s'
              }}
              onMouseEnter={e => e.currentTarget.style.background = theme.bgInput}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <Coffee size={20} color={theme.primary} />
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: theme.text }}>
                    {lang === 'ar' ? tn.name_ar : tn.name_en}
                  </div>
                  <div style={{ fontSize: 12, color: theme.textSecondary, marginTop: 2 }}>
                    {tn.subscription_tier?.toUpperCase()} · {enabledCount}/{CLIENT_MODULES.length} {t.modulesActive}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Badge variant={tn.subscription_tier === 'enterprise' ? 'primary' : tn.subscription_tier === 'growth' ? 'info' : 'warning'}>
                  {tn.subscription_tier}
                </Badge>
                {isExpanded
                  ? <ChevronUp size={16} color={theme.textTertiary} />
                  : <ChevronDown size={16} color={theme.textTertiary} />
                }
              </div>
            </div>

            {isExpanded && (
              <div style={{
                borderTop: `1px solid ${theme.border}`,
                padding: '8px 22px 16px'
              }}>
                {CLIENT_MODULES.map(module => {
                  const Icon = module.icon;
                  const isEnabled = featuresMap[tn.id]?.[module.featureKey] || false;
                  return (
                    <div key={module.key} style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 0',
                      borderBottom: `1px solid ${theme.borderLight}`
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Icon size={16} color={isEnabled ? theme.primary : theme.textTertiary} />
                        <span style={{ fontSize: 13, color: theme.text, fontWeight: 500 }}>
                          {t[module.translationKey]}
                        </span>
                      </div>
                      <Toggle
                        checked={isEnabled}
                        onChange={() => toggleFeature(tn.id, module.featureKey)}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
